import {useEffect, useState} from "react";
import {Checkbox, TextInput} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {dibAppApi} from "../../../../dibAppApi/dibAppApi";
import {ChecklistModal} from "../ChecklistModal";
import {buildCollectionUrl, type TopicMeta} from "./utils";
import classes from "../modalStyles.module.css";

type DuplicateFillingModalProps = {
    opened: boolean;
    onClose: () => void;
    collectionId: string;
    collectionName: string;
    topicMeta: TopicMeta;
    hasOnlyInfoNodes: boolean;
};

const initialOptions = {
    comment: true,
    na: true,
    ok: false,
    deviation: false,
    variables: true,
};

const rowClassNames = {
    root: classes.modalRow,
    labelWrapper: classes.modalRowLabelWrapper,
    label: classes.modalRowLabel,
};

export const DuplicateFillingModal = ({
    opened,
    onClose,
    collectionId,
    collectionName,
    topicMeta,
    hasOnlyInfoNodes,
}: DuplicateFillingModalProps) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [duplicateCollection, {data, isLoading}] =
        dibAppApi.endpoints.duplicateCollection.useMutation();

    const [title, setTitle] = useState(
        t("Phrase.DuplicateOfTitle", {fillingTitle: collectionName}),
    );
    const [options, setOptions] = useState(initialOptions);

    const toggle = (key: keyof typeof initialOptions) =>
        setOptions((prev) => ({...prev, [key]: !prev[key]}));

    const isValid =
        title.length > 0 &&
        (hasOnlyInfoNodes ||
            options.comment ||
            options.na ||
            options.ok ||
            options.deviation ||
            options.variables);

    const handleDuplicate = () => {
        if (!title) return;
        duplicateCollection({collectionId, name: title, ...options});
    };

    useEffect(() => {
        if (data) {
            navigate(
                buildCollectionUrl(topicMeta, {
                    name: data.name,
                    id: data.collectionId.toLowerCase(),
                }),
            );
            onClose();
        }
    }, [data, navigate, onClose, topicMeta]);

    return (
        <ChecklistModal
            opened={opened}
            onClose={onClose}
            title={t("Action.DuplicateFilling")}
            primary={{
                label: t("Word.Duplicate"),
                onClick: handleDuplicate,
                loading: isLoading,
                disabled: !isValid,
                title: isValid
                    ? t("Action.DuplicateFilling")
                    : t("Warn.DataToDuplicateNotChosen"),
            }}
            secondary={{label: t("common.cancel"), onClick: onClose}}
        >
            <div className={classes.modalBody}>
                <TextInput
                    label={t("Label.DuplicateName")}
                    value={title}
                    onChange={(e) => setTitle(e.currentTarget.value)}
                    classNames={{
                        root: classes.selectRoot,
                        label: classes.selectLabel,
                    }}
                />

                {!hasOnlyInfoNodes && (
                    <div className={classes.modalSection}>
                        <div className={classes.subheader}>
                            {t("Info.DataToDuplicate")}
                        </div>
                        <div className={classes.modalRowList}>
                            <Checkbox
                                color="gneis.9"
                                label={t("Word.Comments")}
                                checked={options.comment}
                                onChange={() => toggle("comment")}
                                classNames={rowClassNames}
                            />
                            <Checkbox
                                color="gneis.9"
                                label={`${t("Word.Status")}: ${t("Status.NA")}`}
                                checked={options.na}
                                onChange={() => toggle("na")}
                                classNames={rowClassNames}
                            />
                            <Checkbox
                                color="gneis.9"
                                label={`${t("Word.Status")}: ${t("Status.Ok")}`}
                                checked={options.ok}
                                onChange={() => toggle("ok")}
                                classNames={rowClassNames}
                            />
                            <Checkbox
                                color="gneis.9"
                                label={`${t("Word.Status")}: ${t("Status.Deviation")}`}
                                checked={options.deviation}
                                onChange={() => toggle("deviation")}
                                classNames={rowClassNames}
                            />
                            <Checkbox
                                color="gneis.9"
                                label={t("Phrase.FieldsInDocumentGenerator")}
                                checked={options.variables}
                                onChange={() => toggle("variables")}
                                classNames={rowClassNames}
                            />
                        </div>
                    </div>
                )}
            </div>
        </ChecklistModal>
    );
};
